import { getCampaigns } from 'actions/campains'
import { selectCurrentHotelId, selectCurrentHotelName } from 'accessors'
import {
  PrimaryRegularButton,
  SecondaryRegularButton
} from 'components/ReusableComponents/Button/RegularButtons'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Toggle from 'components/ReusableComponents/Toggle'
import { useInternalAdmin } from 'hooks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import campaignApi from 'services/api/campaign'
import contentApi from 'services/api/content'

import CampaignStepCard from './CampaignStepCard'
import CampaignStrategyCard from './CampaignStrategyCard'
import {
  buildStepMetrics,
  EMPTY_STEP_METRICS,
  formatCount,
  formatEntryDescription,
  formatTimingLabel,
  getAnalyticsDateRange,
  hoursToDays,
  interpolateCampaignPreview,
  OTA_BUSTER_CAMPAIGN_STEPS,
  OTA_BUSTER_CONTENT_CATEGORIES,
  OTA_BUSTER_ENTRY
} from './campaignFlowMock'
import EditStepDialog from './EditStepDialog'
import styles from './OtaBuster.module.scss'
import PeriodSelector from './PeriodSelector'
import SavibookPreviewModal from './SavibookPreviewModal'
import TimingControl from './TimingControl'

function GuestsIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M9 11a3.5 3.5 0 100-7 3.5 3.5 0 000 7zM17 11.5a2.75 2.75 0 100-5.5 2.75 2.75 0 000 5.5zM4 18.5c0-2.485 2.015-4.5 4.5-4.5h1c2.485 0 4.5 2.015 4.5 4.5V19H4v-.5zM14.5 16.5c.759 0 1.458.232 2.042.627A4.25 4.25 0 0119 19.5V20h-3.5v-.5c0-.947-.308-1.82-.83-2.53.43-.33.95-.47 1.33-.47z"
        fill="currentColor"
      />
    </svg>
  )
}

function PlayIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
      <path
        d="M4.5 3.25l7 4.25-7 4.25V3.25z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="0.5"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function normalizeStepContent(content) {
  if (!content) {
    return null
  }

  return {
    isActive: content.isActive ?? content.IsActive ?? true,
    messageTemplate: content.messageTemplate ?? content.MessageTemplate ?? '',
    hoursBeforeCheckIn: content.hoursBeforeCheckIn ?? content.HoursBeforeCheckIn ?? null,
    landing: content.landing ?? content.Landing ?? {},
    incentive: content.incentive ?? content.Incentive ?? {}
  }
}

function buildDraftPayload(data, step) {
  const payload = {
    isActive: data.isEnabled,
    medium: 'EmailAndText',
    messageTemplate: data.messageTemplate,
    landing: {
      header: data.landing?.header || '',
      body: data.landing?.body || '',
      cta: data.landing?.cta || '',
      successTitle: data.landing?.successTitle || '',
      successBody: data.landing?.successBody || '',
      successClosing: data.landing?.successClosing || ''
    },
    incentive: {
      promotionCode: data.incentive?.promotionCode || '',
      promotionTagline: data.incentive?.promotionTagline || '',
      minNightsToStay: data.incentive?.minNightsToStay || 1,
      maxDiscountPercentage: data.incentive?.maxDiscountPercentage ?? 0
    }
  }

  if (step.timingKind === 'daysBeforeCheckIn') {
    payload.hoursBeforeCheckIn = data.daysBeforeCheckIn
  }

  return payload
}

function OtaBusterGuestJourney() {
  const dispatch = useDispatch()
  const { addToast } = useToasts()
  const isInternalAdmin = useInternalAdmin()
  const hotelId = useSelector(selectCurrentHotelId)
  const hotelNameFromStore = useSelector(selectCurrentHotelName)
  const isDemoMode = useSelector(s => s.login.isDemoMode)
  const campaigns = useSelector(s => s.campaigns.content[hotelId])
  const isLoadingCampaigns = useSelector(s => s.campaigns.isLoadingCampaigns)

  const [previewStep, setPreviewStep] = useState(null)
  const [previewLanding, setPreviewLanding] = useState(null)
  const [editingStepId, setEditingStepId] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isLaunching, setIsLaunching] = useState(false)
  const [isUpdatingPause, setIsUpdatingPause] = useState(false)
  const [isEnsuringContents, setIsEnsuringContents] = useState(false)
  const [manageState, setManageState] = useState(null)
  const [isLoadingManage, setIsLoadingManage] = useState(true)
  const [analytics, setAnalytics] = useState(null)
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(true)
  const [periodDays, setPeriodDays] = useState('30')

  const hotelName = isDemoMode
    ? 'The Luxury Resort & Spa'
    : hotelNameFromStore || 'the hotel'
  const firstName = 'Joe'

  const fetchManageState = useCallback(async () => {
    if (!hotelId) {
      return
    }

    setIsLoadingManage(true)
    try {
      const data = await contentApi.getOtaBusterManage()
      setManageState(data)
    } catch (error) {
      console.error('Failed to fetch OTA Buster manage state:', error)
      setManageState(null)
    } finally {
      setIsLoadingManage(false)
    }
  }, [hotelId])

  useEffect(() => {
    if (!hotelId) {
      return undefined
    }

    let cancelled = false

    const loadAndEnsureContents = async () => {
      try {
        const list = await campaignApi.getList(hotelId)
        if (cancelled) return

        const items = list?.items || []
        const present = new Set(
          items
            .filter(campaign => OTA_BUSTER_CONTENT_CATEGORIES.includes(campaign.category))
            .map(campaign => campaign.category)
        )
        const missing = OTA_BUSTER_CONTENT_CATEGORIES.some(category => !present.has(category))

        if (missing) {
          setIsEnsuringContents(true)
          await campaignApi.ensureOtaBusterCampaignContents()
        }

        if (!cancelled) {
          await dispatch(getCampaigns(hotelId))
          await fetchManageState()
        }
      } catch (error) {
        console.error('Failed to ensure OTA Buster campaign contents:', error)
        if (!cancelled) {
          addToast('Could not create OTA Buster email content for this hotel', {
            appearance: 'error',
            autoDismiss: true
          })
          dispatch(getCampaigns(hotelId))
          fetchManageState()
        }
      } finally {
        if (!cancelled) {
          setIsEnsuringContents(false)
        }
      }
    }

    loadAndEnsureContents()

    return () => {
      cancelled = true
    }
  }, [addToast, dispatch, fetchManageState, hotelId])

  const fetchAnalytics = useCallback(async () => {
    if (!hotelId) {
      return
    }

    setIsLoadingAnalytics(true)
    try {
      const { startDate, endDate } = getAnalyticsDateRange(periodDays)
      const data = await contentApi.getOtaBusterAnalytics({ startDate, endDate })
      setAnalytics(data)
    } catch (error) {
      console.error('Failed to fetch OTA Buster analytics:', error)
      setAnalytics(null)
    } finally {
      setIsLoadingAnalytics(false)
    }
  }, [hotelId, periodDays])

  useEffect(() => {
    fetchAnalytics()
  }, [fetchAnalytics])

  const stepMetrics = useMemo(() => buildStepMetrics(analytics), [analytics])

  const manageStepsBySlug = useMemo(() => {
    const map = {}
    ;(manageState?.steps || []).forEach(item => {
      map[item.stepSlug] = item
    })
    return map
  }, [manageState])

  const sequencePreviewBySlug = useMemo(() => {
    const map = {}
    ;(manageState?.sequencePreview || []).forEach(item => {
      map[item.stepSlug] = item
    })
    return map
  }, [manageState])

  const otaCampaignsByCategory = useMemo(() => {
    const map = {}
    ;(campaigns || [])
      .filter(campaign => OTA_BUSTER_CONTENT_CATEGORIES.includes(campaign.category))
      .forEach(campaign => {
        map[campaign.category] = campaign
      })
    return map
  }, [campaigns])

  const getStepState = useCallback(
    step => {
      const manageStep = manageStepsBySlug[step.id]
      const draft = normalizeStepContent(manageStep?.draft)
      const campaign = otaCampaignsByCategory[step.contentCategory]
      const daysBeforeCheckIn =
        draft?.hoursBeforeCheckIn ??
        hoursToDays(campaign?.hoursAfterPreviousCampaign, step.defaultDaysBeforeCheckIn)

      return {
        isActive:
          draft?.isActive ?? (campaign?.isActive == null ? true : Boolean(campaign.isActive)),
        hasEmailCampaign:
          campaign?.hasEmailCampaign == null ? true : Boolean(campaign.hasEmailCampaign),
        contentId:
          manageStep?.notificationContentId ??
          campaign?.contentId ??
          campaign?.notificationContentId ??
          null,
        messageTemplate: draft?.messageTemplate || step.preview,
        landing: draft?.landing || {},
        incentive: draft?.incentive || {},
        daysBeforeCheckIn,
        hasDraftChanges: Boolean(manageStep?.hasDraftChanges),
        timingLabel: sequencePreviewBySlug[step.id]?.timingLabel
      }
    },
    [manageStepsBySlug, otaCampaignsByCategory, sequencePreviewBySlug]
  )

  const saveStepDraft = async (step, data) => {
    const state = getStepState(step)
    if (!state.contentId) {
      throw new Error('Campaign content is not ready yet')
    }

    await contentApi.saveOtaBusterStepDraft(state.contentId, buildDraftPayload(data, step))
    await fetchManageState()
  }

  const handleDialogSave = async data => {
    const step = OTA_BUSTER_CAMPAIGN_STEPS.find(item => item.id === editingStepId)
    if (!step) return

    setIsSaving(true)
    try {
      await saveStepDraft(step, data)
      addToast('Draft saved — launch the campaign to start sending', {
        appearance: 'success',
        autoDismiss: true
      })
      setEditingStepId(null)
      fetchAnalytics()
    } catch (error) {
      console.error('Failed to save OTA Buster step draft:', error)
      addToast('Failed to save draft', {
        appearance: 'error',
        autoDismiss: true
      })
    } finally {
      setIsSaving(false)
    }
  }

  const buildDraftFromState = step => {
    const state = getStepState(step)
    return {
      isEnabled: state.isActive,
      daysBeforeCheckIn: state.daysBeforeCheckIn,
      messageTemplate: state.messageTemplate,
      landing: state.landing,
      incentive: state.incentive
    }
  }

  const handleEnableStep = async step => {
    try {
      await saveStepDraft(step, {
        ...buildDraftFromState(step),
        isEnabled: true
      })
      addToast(`${step.title} enabled in draft`, {
        appearance: 'success',
        autoDismiss: true
      })
      fetchAnalytics()
    } catch (error) {
      console.error('Failed to enable OTA Buster step:', error)
      addToast('Failed to enable step', {
        appearance: 'error',
        autoDismiss: true
      })
    }
  }

  const handlePauseStep = async step => {
    try {
      await saveStepDraft(step, {
        ...buildDraftFromState(step),
        isEnabled: false
      })
      addToast(`${step.title} paused in draft`, {
        appearance: 'success',
        autoDismiss: true
      })
      fetchAnalytics()
    } catch (error) {
      console.error('Failed to pause OTA Buster step:', error)
      addToast('Failed to pause step', {
        appearance: 'error',
        autoDismiss: true
      })
    }
  }

  const handleTimingDaysChange = async (step, daysBeforeCheckIn) => {
    if (!daysBeforeCheckIn || daysBeforeCheckIn < 1 || daysBeforeCheckIn > 14) {
      return
    }

    try {
      await saveStepDraft(step, {
        ...buildDraftFromState(step),
        daysBeforeCheckIn
      })
      fetchAnalytics()
    } catch (error) {
      console.error('Failed to update OTA Buster step timing:', error)
      addToast('Failed to update timing', {
        appearance: 'error',
        autoDismiss: true
      })
    }
  }

  const handleLaunch = async () => {
    setIsLaunching(true)
    try {
      await contentApi.launchOtaBusterCampaign()
      await fetchManageState()
      dispatch(getCampaigns(hotelId))
      addToast('Campaign launched — guests will receive published content', {
        appearance: 'success',
        autoDismiss: true
      })
    } catch (error) {
      console.error('Failed to launch OTA Buster campaign:', error)
      addToast('Failed to launch campaign', {
        appearance: 'error',
        autoDismiss: true
      })
    } finally {
      setIsLaunching(false)
    }
  }

  const handlePauseToggle = async event => {
    const shouldPause = !event.target.checked
    setIsUpdatingPause(true)
    try {
      if (shouldPause) {
        await contentApi.pauseOtaBusterCampaign()
      } else {
        await contentApi.resumeOtaBusterCampaign()
      }
      await fetchManageState()
      addToast(shouldPause ? 'Campaign paused' : 'Campaign resumed', {
        appearance: 'success',
        autoDismiss: true
      })
    } catch (error) {
      console.error('Failed to update OTA Buster pause state:', error)
      addToast('Failed to update campaign pause state', {
        appearance: 'error',
        autoDismiss: true
      })
    } finally {
      setIsUpdatingPause(false)
    }
  }

  const handlePreviewSavibook = step => {
    const state = getStepState(step)
    setPreviewLanding(state.landing)
    setPreviewStep(step)
  }

  const handleManageChannelStrategy = () => {
    addToast('Channel strategy management is coming soon', {
      appearance: 'info',
      autoDismiss: true
    })
  }

  const editingStep = OTA_BUSTER_CAMPAIGN_STEPS.find(step => step.id === editingStepId)
  const editingState = editingStep ? getStepState(editingStep) : null
  const isCampaignPaused = Boolean(manageState?.isCampaignPaused)
  const hasUnpublishedChanges = Boolean(manageState?.hasUnpublishedChanges)
  const isLoading =
    isLoadingCampaigns || isEnsuringContents || isLoadingAnalytics || isLoadingManage

  if (!isInternalAdmin) {
    return (
      <div className={styles.accessDenied} data-testid="ota-buster-access-denied">
        Access denied. OTA Buster is available to internal admins only.
      </div>
    )
  }

  return (
    <div className={styles.page} data-testid="ota-buster-guest-journey">
      <CustomHeader title="OTA Buster" width={1120} />

      <div className={styles.wrapper}>
        <div className={styles.pageHero}>
          <h1 className={styles.pageTitle}>OTA Buster - Guest journey</h1>
          <p className={styles.pageSubtitle}>
            Turn commission-heavy OTA guests into repeat direct guests, stage by stage.
          </p>
        </div>

        <section className={styles.journeySection}>
          <div className={styles.journeySectionHeader}>
            <div>
              <h2 className={styles.sectionHeading}>Guest journey</h2>
              <p className={styles.sectionDescription}>
                Every stage a guest moves through. Adjust the timing here, or open a stage
                to edit the guest experience, its offer and its conditions.
              </p>
            </div>
            <PeriodSelector
              value={periodDays}
              onChange={setPeriodDays}
              disabled={isLoading}
            />
          </div>

          <div className={styles.campaignControls}>
            <label className={styles.pauseToggle}>
              <Toggle
                checked={!isCampaignPaused}
                handleChange={handlePauseToggle}
                isGray={false}
                disabled={isUpdatingPause || isLoadingManage}
              />
              <span className={styles.pauseLabel}>
                {isCampaignPaused ? 'Campaign paused' : 'Campaign active'}
              </span>
            </label>

            {hasUnpublishedChanges && (
              <span className={styles.unpublishedBadge} data-testid="ota-buster-unpublished-badge">
                Unpublished changes
              </span>
            )}
          </div>

          {(hasUnpublishedChanges || manageState?.lastPublishedOn) && (
            <div className={styles.publishBar} data-testid="ota-buster-publish-bar">
              <div className={styles.publishCopy}>
                {hasUnpublishedChanges ? (
                  <p>
                    Draft edits are saved but not sent to guests until you launch the
                    campaign.
                  </p>
                ) : (
                  <p>Campaign is live. Edits require saving a draft and launching again.</p>
                )}
              </div>
              <div className={styles.publishActions}>
                <SecondaryRegularButton onClick={fetchManageState} disabled={isLoadingManage}>
                  Refresh
                </SecondaryRegularButton>
                <PrimaryRegularButton
                  onClick={handleLaunch}
                  disabled={!hasUnpublishedChanges || isLaunching}
                >
                  {isLaunching ? 'Launching…' : 'Launch campaign'}
                </PrimaryRegularButton>
              </div>
            </div>
          )}

          <CampaignStrategyCard onManageStrategy={handleManageChannelStrategy} />

          {isLoading ? (
            <LoadingIndicator />
          ) : (
            <>
              <div className={styles.entryCard}>
                <div className={styles.entryLeft}>
                  <span className={styles.entryIcon}>
                    <GuestsIcon />
                  </span>
                  <div className={styles.entryCopy}>
                    <span className={styles.entryTitle}>{OTA_BUSTER_ENTRY.title}</span>
                    <span className={styles.entryDescription}>
                      {formatEntryDescription(analytics?.otaBookedGuests)}
                    </span>
                  </div>
                </div>
                <div className={styles.entryRight}>
                  <GuestsIcon />
                  <span>{formatCount(analytics?.otaBookedGuests)} guests</span>
                </div>
              </div>

              {isCampaignPaused && (
                <div className={styles.pausedBanner} data-testid="ota-buster-paused-banner">
                  Campaign is paused — no new OTA Buster messages will be scheduled until
                  you resume.
                </div>
              )}

              <div className={styles.campaignStarts}>
                <span className={styles.campaignStartsIcon}>
                  <PlayIcon />
                </span>
                <span>Campaign starts</span>
              </div>

              <div className={styles.flow}>
                {OTA_BUSTER_CAMPAIGN_STEPS.map(step => {
                  const state = getStepState(step)
                  const timingLabel =
                    state.timingLabel || formatTimingLabel(step, state.daysBeforeCheckIn)

                  return (
                    <div key={step.id} className={styles.flowItem}>
                      <TimingControl
                        label={timingLabel}
                        editable={step.timingKind === 'daysBeforeCheckIn'}
                        daysValue={
                          state.daysBeforeCheckIn || step.defaultDaysBeforeCheckIn || 1
                        }
                        onDaysChange={days => handleTimingDaysChange(step, days)}
                        onClick={() => setEditingStepId(step.id)}
                      />
                      <CampaignStepCard
                        step={step}
                        metrics={stepMetrics[step.id] || EMPTY_STEP_METRICS}
                        preview={interpolateCampaignPreview(
                          state.messageTemplate.includes('{first}')
                            ? state.messageTemplate
                            : step.preview,
                          {
                            firstName,
                            hotelName
                          }
                        )}
                        isActive={state.isActive}
                        hasEmailCampaign={state.hasEmailCampaign}
                        hasDraftChanges={state.hasDraftChanges}
                        onEdit={() => setEditingStepId(step.id)}
                        onEnable={() => handleEnableStep(step)}
                        onPause={() => handlePauseStep(step)}
                        onPreviewSavibook={handlePreviewSavibook}
                      />
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </section>
      </div>

      {previewStep && (
        <SavibookPreviewModal
          step={previewStep}
          hotelName={hotelName}
          firstName={firstName}
          landing={previewLanding}
          onClose={() => {
            setPreviewStep(null)
            setPreviewLanding(null)
          }}
        />
      )}

      {editingStep && (
        <EditStepDialog
          key={editingStep.id}
          contentId={editingState.contentId}
          contentCategory={editingStep.contentCategory}
          title={`Edit ${editingStep.title}`}
          hotelName={hotelName}
          firstName={firstName}
          onClose={() => setEditingStepId(null)}
          onSave={handleDialogSave}
          isSaving={isSaving}
          timingKind={editingStep.timingKind}
          timingLabel={
            editingState.timingLabel ||
            formatTimingLabel(editingStep, editingState.daysBeforeCheckIn)
          }
          initialData={{
            isEnabled: editingState.isActive,
            daysBeforeCheckIn:
              editingState.daysBeforeCheckIn || editingStep.defaultDaysBeforeCheckIn || 1,
            messageTemplate: editingState.messageTemplate,
            landing: editingState.landing,
            incentive: editingState.incentive
          }}
        />
      )}
    </div>
  )
}

export default OtaBusterGuestJourney
